// The matching engine for the "Which show should I see?" page.
// It reads each show's rec_profile (saved by set_show_profiles.py), so new shows join automatically.

const TASTE = ["funny", "moving", "thrilling", "spectacle", "thoughtful", "romantic"];
const FEEL_TRAIT = { laugh: "funny", moved: "moving", thrill: "thrilling", escape: "spectacle", think: "thoughtful", love: "romantic" };
const STORY_TRAIT = { magic: "spectacle", history: "thoughtful", love: "romantic", dark: "thrilling", quirky: "funny" };
const LENGTH_LIMIT = { short: 130, normal: 175, long: 9999 };

export function lowestPrice(show) {
  let low = null;
  for (const t of show.ticket_links || []) {
    const m = (t.starting_from || "").match(/[\d,]+\.?\d*/);
    if (!m) continue;
    const v = parseFloat(m[0].replace(/,/g, ""));
    if (!Number.isNaN(v) && (low === null || v < low)) low = v;
  }
  return low;
}

// Hard rules. Age and "avoid" are never relaxed; length and budget may be if nothing else fits.
export function eligible(show, a, relax = {}) {
  const p = show.rec_profile;
  if (!p || show.status === "Closed" || show.is_sample) return false;
  if (p.min_age > a.youngest) return false;
  // a family with children under 14 only gets shows marked as suitable for families, not just old enough
  if (a.party === "family" && a.youngest < 14 && !(p.best_for || []).includes("family")) return false;
  if ((a.avoid || []).some((f) => (p.flags || []).includes(f))) return false;
  if (!relax.length && p.mins > LENGTH_LIMIT[a.length || "long"]) return false;
  if (!relax.budget && a.budget) {
    const low = lowestPrice(show);
    if (low !== null && low > a.budget) return false;
  }
  return true;
}

const closeness = (value, target) => 1 - Math.abs(value - target) / 5;

// How closely the show's character points the same way as what you asked for (cosine similarity).
// This favours shows that are strong on the things you want, not shows that are good at everything.
function moodScore(p, a) {
  const w = {};
  const add = (t, x) => {
    if (t) w[t] = (w[t] || 0) + x;
  };
  add(FEEL_TRAIT[a.feel], 2);
  add(STORY_TRAIT[a.story], 1);
  const keys = Object.keys(w);
  if (!keys.length) return 0.5;
  const dot = keys.reduce((s, t) => s + w[t] * (p.taste[t] || 0), 0);
  const wn = Math.sqrt(keys.reduce((s, t) => s + w[t] * w[t], 0));
  const sn = Math.sqrt(TASTE.reduce((s, t) => s + (p.taste[t] || 0) ** 2, 0)) || 1;
  return dot / (wn * sn);
}

function axesScore(p, a) {
  const items = [];
  if (a.music === "songs") items.push([1, closeness(p.music, 5)]);
  else if (a.music === "some") items.push([0.6, closeness(p.music, 3)]);
  else if (a.music === "none") items.push([1, closeness(p.music, 0)]);
  if (a.scale === "big") items.push([1, closeness(p.scale, 5)]);
  else if (a.scale === "close") items.push([1, closeness(p.scale, 1)]);
  if (a.familiar === "known") items.push([1, closeness(p.known, 5)]);
  else if (a.familiar === "new") items.push([1, closeness(p.known, 1)]);
  else if (a.frequency === "first") items.push([0.6, closeness(p.known, 4)]);
  const total = items.reduce((s, [w]) => s + w, 0);
  return total ? items.reduce((s, [w, c]) => s + w * c, 0) / total : 0.5;
}

export function scoreShow(show, a, nudges = {}) {
  const p = show.rec_profile;
  let s = 0.6 * moodScore(p, a) + 0.3 * axesScore(p, a);
  if (a.stars === "yes") s += 0.15 * (p.stars / 5);
  else if (a.stars === "bonus") s += 0.04 * (p.stars / 5);
  if ((p.best_for || []).includes(a.party)) s += a.party === "family" ? 0.08 : 0.05;
  if (show.status === "Coming Soon") s -= 0.03;
  const set = nudges[a.party === "family" ? "family" : "adult"] || {};
  return s + (set[show.name] || 0);
}

const FEEL_WORDS = {
  funny: "properly funny", moving: "moving enough to need a tissue", thrilling: "an edge-of-your-seat thriller",
  spectacle: "visually spectacular", thoughtful: "gives you plenty to think about", romantic: "a big love story",
};
const STORY_WORDS = {
  spectacle: "full of magic and wonder", thoughtful: "based on real history", romantic: "a love story at heart",
  thrilling: "dark and twisty", funny: "wonderfully quirky",
};

export function reasonsFor(show, a) {
  const p = show.rec_profile;
  const out = [];
  const ft = FEEL_TRAIT[a.feel];
  if (ft && p.taste[ft] >= 4) out.push(FEEL_WORDS[ft]);
  const st = STORY_TRAIT[a.story];
  if (st && p.taste[st] >= 4 && st !== ft) out.push(STORY_WORDS[st]);
  if (a.music === "songs" && p.music >= 4) out.push("songs all the way through");
  if (a.music === "none" && p.music === 0) out.push("no singing, just a play");
  if (a.scale === "big" && p.scale >= 4) out.push("grand in scale");
  if (a.scale === "close" && p.scale <= 2) out.push("intimate and close-up");
  if (a.familiar === "known" && p.known >= 4) out.push("a famous favourite");
  if (a.familiar === "new" && p.known <= 2) out.push("something fresh");
  if ((a.stars === "yes" || a.stars === "bonus") && p.stars >= 4) out.push("star casting right now");
  if (a.length === "short" && p.mins <= 130) out.push(`short, at about ${p.mins} minutes`);
  const partyWords = { date: "a good date night", friends: "a great one for friends", family: "suits a family outing", solo: "perfect on your own" };
  if ((p.best_for || []).includes(a.party)) out.push(partyWords[a.party]);
  return out.slice(0, 3);
}

// The shows that pass the hard rules, loosening time and then budget only if fewer than three fit.
export function buildPool(shows, a) {
  const relax = {};
  const relaxed = [];
  let pool = shows.filter((s) => eligible(s, a, relax));
  for (const [key, applies] of [["length", a.length && a.length !== "long"], ["budget", Boolean(a.budget)]]) {
    if (pool.length >= 3) break;
    if (!applies) continue;
    relax[key] = true;
    const wider = shows.filter((s) => eligible(s, a, relax));
    if (wider.length > pool.length) relaxed.push(key);
    pool = wider;
  }
  return { pool, relaxed };
}

// Returns up to three picks, plus anything we had to loosen to find them.
export function recommend(shows, a, nudges = {}) {
  const { pool, relaxed } = buildPool(shows, a);
  const picks = pool
    .map((show) => ({ show, score: scoreShow(show, a, nudges) }))
    .sort((x, y) => y.score - x.score || x.show.name.localeCompare(y.show.name))
    .slice(0, 3)
    .map((r) => ({ ...r, reasons: reasonsFor(r.show, a), price: lowestPrice(r.show) }));
  return { picks, relaxed, eligibleCount: pool.length };
}

export const ALLOWED = {
  party: ["solo", "date", "friends", "family"], youngest: [3, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 18],
  frequency: ["first", "sometimes", "often"], feel: Object.keys(FEEL_TRAIT), music: ["songs", "some", "none", "any"],
  story: Object.keys(STORY_TRAIT), scale: ["big", "close", "any"], familiar: ["known", "new", "any"],
  stars: ["yes", "no", "bonus"], length: ["short", "normal", "long"], budget: [30, 50, 80, 120, 0],
  avoid: ["language", "heavy", "loud", "participation"],
};
export { TASTE };

// ---- keeping the answers in the page address, so a result survives leaving and coming back ----
const KEYS = { party: "party", frequency: "often", feel: "feel", music: "music", story: "story", scale: "scale", familiar: "known", stars: "star", length: "time", budget: "budget" };

export function answersToQuery(a) {
  const p = new URLSearchParams();
  for (const [k, q] of Object.entries(KEYS)) p.set(q, String(a[k]));
  if (a.party === "family") p.set("age", String(a.youngest));
  if ((a.avoid || []).length) p.set("avoid", a.avoid.join("."));
  return p.toString();
}

// Returns the answers, or null if the address is incomplete or has been tampered with.
export function queryToAnswers(params) {
  const a = {};
  for (const [k, q] of Object.entries(KEYS)) {
    const v = params.get(q);
    if (v === null) return null;
    if (k === "budget") {
      const n = Number(v);
      if (!ALLOWED.budget.includes(n)) return null;
      a.budget = n;
    } else {
      if (!ALLOWED[k].includes(v)) return null;
      a[k] = v;
    }
  }
  if (a.party === "family") {
    const n = Number(params.get("age"));
    if (!ALLOWED.youngest.includes(n) || n === 18) return null;
    a.youngest = n;
  } else {
    a.youngest = 18;
  }
  const avoid = (params.get("avoid") || "").split(".").filter(Boolean);
  if (avoid.some((f) => !ALLOWED.avoid.includes(f))) return null;
  a.avoid = avoid;
  return a;
}